import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, X } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Modal, Empty } from "@/components/ui-bits";

const url = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/progress-photos/${p.id}?auth=${localStorage.getItem("ip_token")}`;

export const ProgressPhotos = ({ photos = [], uploadPath, onChanged, canDelete = true }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // file awaiting date/note
  const [meta, setMeta] = useState({ takenAt: new Date().toISOString().slice(0, 10), note: "" });
  const [compare, setCompare] = useState([]);
  const [open, setOpen] = useState(null);
  const sorted = photos.slice().sort((a, b) => a.takenAt.localeCompare(b.takenAt));

  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setPending(f); };
  const upload = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("file", pending);
    fd.append("takenAt", `${meta.takenAt}T00:00:00Z`);
    if (meta.note) fd.append("note", meta.note);
    try { await api.post(uploadPath, fd); toast.success("Progress photo added"); setPending(null); setMeta({ ...meta, note: "" }); onChanged(); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    try { await api.delete(`/progress-photos/${p.id}`); setOpen(null); setCompare((c) => c.filter((x) => x.id !== p.id)); onChanged(); } catch (ex) { toast.error(errMsg(ex)); }
  };
  const toggleCompare = (p) => setCompare((c) => c.some((x) => x.id === p.id) ? c.filter((x) => x.id !== p.id) : [...c, p].slice(-2));
  const [a, b] = compare.slice().sort((x, y) => x.takenAt.localeCompare(y.takenAt));

  return (
    <div data-testid="progress-photos">
      <div className="ph" style={{ marginBottom: 8 }}>
        <div className="ss">{sorted.length} photo{sorted.length !== 1 ? "s" : ""}{sorted.length >= 2 ? " · tap two to compare" : ""}</div>
        <button className="btn sm g" disabled={busy} onClick={() => input.current?.click()} data-testid="progress-photo-add-button"><Camera size={13} style={{ marginRight: 5, verticalAlign: -2 }} />Add photo</button>
        <input ref={input} type="file" accept="image/*" hidden onChange={pick} data-testid="progress-photo-file-input" />
      </div>
      {sorted.length === 0 && <Empty>No progress photos yet — add your first one to start the timeline.</Empty>}
      <div className="ptimeline">
        {sorted.map((p) => (
          <div key={p.id} className={`pthumb ${compare.some((x) => x.id === p.id) ? "sel" : ""}`} data-testid={`progress-photo-${p.id}`}>
            <img src={url(p)} alt={p.note || fmtDate(p.takenAt)} onClick={() => setOpen(p)} loading="lazy" />
            <div className="pdate">{fmtDate(p.takenAt, { day: "numeric", month: "short", year: "2-digit" })}</div>
            <button type="button" className="pcmp" onClick={() => toggleCompare(p)} data-testid={`progress-compare-${p.id}`}>{compare.some((x) => x.id === p.id) ? "✓" : "⇄"}</button>
          </div>
        ))}
      </div>
      {a && b && (
        <div className="pcompare" data-testid="progress-compare">
          {[a, b].map((p, i) => (
            <div key={p.id}>
              <div className="ss" style={{ marginBottom: 4 }}>{i === 0 ? "Before" : "After"} · {fmtDate(p.takenAt, { day: "numeric", month: "short", year: "numeric" })}{p.note ? ` · ${p.note}` : ""}</div>
              <img src={url(p)} alt="" />
            </div>
          ))}
          <div className="ss" style={{ gridColumn: "1 / -1", textAlign: "center" }}>{Math.round((new Date(b.takenAt) - new Date(a.takenAt)) / 86400000)} days apart · <button className="linkbtn" onClick={() => setCompare([])} data-testid="progress-compare-clear">clear</button></div>
        </div>
      )}
      {pending && (
        <Modal title="ADD PROGRESS PHOTO" sub="Date it so the timeline stays honest." onClose={() => setPending(null)} testId="progress-photo-modal">
          <form onSubmit={upload}>
            <img src={URL.createObjectURL(pending)} alt="" style={{ width: "100%", maxHeight: 260, objectFit: "contain", borderRadius: 10, background: "var(--muted)" }} />
            <div className="frow">
              <div><label>Taken on</label><input type="date" required value={meta.takenAt} onChange={(e) => setMeta({ ...meta, takenAt: e.target.value })} data-testid="progress-photo-date-input" /></div>
              <div><label>Note</label><input placeholder="Week 4 · 78.2kg" value={meta.note} onChange={(e) => setMeta({ ...meta, note: e.target.value })} data-testid="progress-photo-note-input" /></div>
            </div>
            <div className="mfoot">
              <button type="button" className="btn g" onClick={() => setPending(null)} data-testid="progress-photo-cancel">Cancel</button>
              <button className="btn" disabled={busy} data-testid="progress-photo-save">{busy ? "Uploading…" : "Save photo"}</button>
            </div>
          </form>
        </Modal>
      )}
      {open && (
        <div className="lightbox" onClick={() => setOpen(null)} data-testid="progress-lightbox">
          <img src={url(open)} alt="" onClick={(e) => e.stopPropagation()} />
          <div className="lb-bar" onClick={(e) => e.stopPropagation()}>
            <span>{fmtDate(open.takenAt, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}{open.note ? ` · ${open.note}` : ""}</span>
            <span style={{ display: "flex", gap: 8 }}>
              {canDelete && <button className="btn sm danger" style={{ background: "transparent" }} onClick={() => remove(open)} data-testid="progress-photo-delete"><Trash2 size={13} /></button>}
              <button className="btn sm g" style={{ color: "#fff", borderColor: "#fff" }} onClick={() => setOpen(null)} data-testid="progress-lightbox-close"><X size={13} /></button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
